import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { DEFAULT_TIME_ZONE, ianaTimezoneSchema } from '../timezone';
import { checkInSignalSchema } from './check-in-signal';

export const CHECK_IN_REMINDER_PREVIEW_MAX_BYTES = 32_768;
const label = z.string().max(160);
const date = z.iso.date();
const recency = checkInSignalSchema.shape.recency.unwrap().extend({
  lastDate: date,
  lastMoods: z.array(label).max(16),
  lastNote: z.string().max(2_000).nullable(),
  lastTagGroups: z.array(z.object({ group: label, tags: z.array(label).max(16) }).strict()).max(16),
});

/** Preview-only bounds; the production signal owner keeps its existing contract. */
export const checkInReminderPreviewRequestSchema = z
  .object({
    checkIn: checkInSignalSchema.extend({
      recency: recency.nullable(),
      lastCheckInDate: date.nullable(),
      daysSinceLastCheckIn: z.number().int().min(0).nullable(),
    }),
    timeZone: ianaTimezoneSchema.default(DEFAULT_TIME_ZONE),
    now: z.iso.datetime({ offset: true }).optional(),
  })
  .strict();
export type CheckInReminderPreviewRequest = z.infer<typeof checkInReminderPreviewRequestSchema>;

const common = {
  modelId: z.string(),
  promptVersion: z.string().nullable(),
  templateVersion: z.string().nullable(),
  attempts: z.array(
    z.object({
      outcome: z.string(),
      turn: z.string().nullable(),
      latencyMs: z.number().nullable(),
      inputTokens: z.number().nullable(),
      outputTokens: z.number().nullable(),
    }),
  ),
};
const copy = { title: z.string(), body: z.string() };
/** All response objects, including nested records, tolerate additive fields. */
export const checkInReminderPreviewDataSchema = z.discriminatedUnion('outcome', [
  z.object({ ...common, ...copy, outcome: z.literal('push') }),
  z.object({ ...common, ...copy, outcome: z.literal('fallback'), reason: z.string() }),
  z.object({ ...common, outcome: z.literal('declined'), reason: z.string() }),
  z.object({
    ...common,
    ...copy,
    outcome: z.literal('guard_rejected'),
    rule: z.string().nullable(),
  }),
  z.object({ ...common, outcome: z.literal('generation_failed'), failureReason: z.string() }),
]);
export type CheckInReminderPreviewData = z.infer<typeof checkInReminderPreviewDataSchema>;
export const checkInReminderPreviewResponseSchema = apiEnvelope(checkInReminderPreviewDataSchema);
